import h from "../helpers";
import { request } from "../helpers/httpInterceptor";
import { notifyGreen } from "./notifyActions";
import { userRolesPermissionsByTypes, wikiPageTypes } from "../components/wiki/constants";
import { incAjaxCount, decAjaxCount } from "./actionCreators";
import { Dispatch, Action } from "redux";
import { ThunkAction } from "redux-thunk";
import {
    ISetLoadingPage,
    ISetSavingPage,
    ISetLoadingImages,
    ISetWikiImages,
    IPage,
    IUpdatePage,
    ISetLoadingPreview,
} from "../types/stores/wiki";
import {
    SET_LOADING_PAGE,
    SET_SAVING_PAGE,
    SET_WIKI_PAGES,
    SET_LOADING_IMAGES,
    UPDATE_WIKI_PAGE_PROPERTY,
} from "../reducers/wiki";
import { IAppState } from "../types/stores";
import { ISetWikiPreview } from "../types/stores/wiki";
import { SET_WIKI_LOADING_PREVIEW, SET_WIKI_PREVIEW } from "../reducers/wiki";

export const SetWikiIsLoadingImages = (newValue: boolean): ISetLoadingImages => ({
    type: SET_LOADING_IMAGES,
    isLoading: newValue,
});

export const SetWikiIsLoading = (newValue: boolean): ISetLoadingPage => ({
    type: SET_LOADING_PAGE,
    isLoading: newValue,
});

export const SetWikiIsSaving = (newValue: boolean): ISetSavingPage => ({
    type: SET_SAVING_PAGE,
    isSaving: newValue,
});

export const SetWikiPreviewLoading = (newValue: boolean): ISetLoadingPreview => ({
    type: SET_WIKI_LOADING_PREVIEW,
    isLoadingPreview: newValue,
});

export const SetWikiPreview = (newValue: string): ISetWikiPreview => ({
    type: SET_WIKI_PREVIEW,
    selectedImagePreview: newValue,
});

export const GetWikiPages =
    (): ThunkAction<void, IAppState, null, Action<string>> =>
    (dispatch, getState): void => {
        const filteredPages: Array<IPage> = [];
        const state: IAppState = getState();
        const roles = state.session.roles || [];
        const pageTypes = new Set();

        dispatch(SetWikiIsLoading(true));
        return request(
            `/api/wiki-pages/all`,
            {
                credentials: "same-origin",
                method: "GET",
            },
            dispatch
        )
            .then(h.checkStatus)
            .then(h.toJson)
            .then((data: { success: boolean; pages: Array<IPage> }) => {
                if (data && data.success) {
                    // this should be on the controller level, we don't have to grab and potentially expose pages if we don't need to - vu
                    if (roles.length == 0) {
                        pageTypes.add(wikiPageTypes.default);
                    } else {
                        for (let i = 0; i < roles.length; i++) {
                            const permittedPageTypes = userRolesPermissionsByTypes[roles[i]];
                            for (let j = 0; j < permittedPageTypes.length; j++) {
                                pageTypes.add(permittedPageTypes[j]);
                            }
                        }
                    }

                    for (let j = 0; j < data.pages.length; j++) {
                        if (pageTypes.has(data.pages[j].Type)) {
                            filteredPages.push(data.pages[j]);
                        }
                    }
                    dispatch(SetWikiIsLoading(false));
                    dispatch({ type: SET_WIKI_PAGES, pages: filteredPages });
                }
            })
            .catch(error => {
                dispatch(SetWikiIsLoading(false));
                h.error("Error getting wiki pages.", error);
            });
    };

export const CreateNewWikiPage =
    (pageData: IPage): ThunkAction<void, IAppState, null, Action<string>> =>
    (dispatch): void => {
        dispatch(SetWikiIsLoading(false));
        return request(
            `/api/wiki-pages/`,
            {
                method: "POST",
                credentials: "same-origin",
                body: JSON.stringify({
                    Title: pageData.Title,
                    Content: pageData.Content,
                    Category: pageData.Category,
                    Type: pageData.Type,
                }),
            },
            dispatch
        )
            .then(h.checkStatus)
            .then(h.toJson)
            .then(data => {
                if (data.success) {
                    dispatch(notifyGreen(`Successfully added the page: "${pageData.Title}".`));
                    dispatch(SetWikiIsLoading(false));
                    dispatch(GetWikiPages());
                }
            })
            .catch(error => {
                dispatch(SetWikiIsLoading(false));
                h.error("Error adding new page", error);
            });
    };

export const DeleteWikiPage =
    (pageId: number): ThunkAction<void, IAppState, null, Action<string>> =>
    (dispatch): void => {
        dispatch(SetWikiIsLoading(false));
        return request(
            `/api/wiki-pages/delete/${pageId}`,
            {
                method: "POST",
                credentials: "same-origin",
            },
            dispatch
        )
            .then(h.checkStatus)
            .then(h.toJson)
            .then(data => {
                if (data.success) {
                    dispatch(notifyGreen(`Successfully removed page.`));
                    dispatch(SetWikiIsSaving(false));
                    dispatch(GetWikiPages());
                }
            })
            .catch(error => {
                dispatch(SetWikiIsSaving(false));
                h.error("Error deleting page", error);
            });
    };

export const UpdateWikiPage =
    (pageId: number, pageData: IPage): ThunkAction<void, IAppState, null, Action<string>> =>
    (dispatch): void => {
        dispatch(SetWikiIsSaving(true));

        return request(
            `/api/wiki-pages/${pageId}`,
            {
                method: "PUT",
                credentials: "same-origin",
                body: JSON.stringify({
                    Title: pageData.Title,
                    Content: pageData.Content,
                    Category: pageData.Category,
                    Type: pageData.Type,
                }),
            },
            dispatch
        )
            .then(h.checkStatus)
            .then(h.toJson)
            .then(data => {
                if (data.success) {
                    dispatch(notifyGreen(`Successfully update the page: "${pageData.Title}".`));
                    dispatch(SetWikiIsSaving(false));
                    dispatch(GetWikiPages());
                }
            })
            .catch(error => {
                dispatch(SetWikiIsSaving(false));
                h.error(`Error updating "${pageData.Title}"`, error);
            });
    };

export const UpdateWikiPagesSortOrder =
    (pagesToUpdate: Array<IUpdatePage>): ThunkAction<void, IAppState, null, Action<string>> =>
    (dispatch): void => {
        // optimistic update, first update the UI for better user experience, then update in the DB.
        pagesToUpdate.forEach(x =>
            dispatch({
                type: UPDATE_WIKI_PAGE_PROPERTY,
                wikiPageId: x.Id,
                property: "SortOrder",
                newValue: x.SortOrder,
            })
        );

        dispatch(incAjaxCount());
        request(
            `/api/wiki-pages/pagesort`,
            {
                method: "POST",
                credentials: "same-origin",
                headers: {
                    "Content-Type": "application/json; charset=utf-8",
                },
                body: JSON.stringify(pagesToUpdate),
            },
            dispatch
        )
            .then(h.checkStatus)
            .then(h.toJson)
            .then(() => {
                dispatch(decAjaxCount());
            })
            .catch(error => {
                h.error(`Error updating page position.`, error);
                dispatch(decAjaxCount());
                dispatch(GetWikiPages());
            });
    };

export const GetWikiImagesList =
    () =>
    (dispatch: Dispatch<ISetLoadingImages | ISetWikiImages>): void => {
        dispatch(SetWikiIsLoadingImages(true));
        request(
            "/api/wiki-pages/images",
            {
                credentials: "same-origin",
                method: "GET",
            },
            dispatch
        )
            .then(h.checkStatus)
            .then(h.toJson)
            .then(data => {
                dispatch(SetWikiIsLoadingImages(false));
                dispatch({
                    type: "SET_WIKI_IMAGES",
                    images: data.images,
                });
            })
            .catch(error => {
                dispatch(SetWikiIsLoadingImages(false));
                h.error("Error fetching available images.", error);
            });
    };

export const addWikiImage =
    (selectedImageContent: string, selectedImageTitleOverride: string, selectedImageCategory: number) =>
    (dispatch: Dispatch<any>): void => {
        const imagesFiles = new FormData();
        dispatch(SetWikiIsSaving(true));
        imagesFiles.append("Image", selectedImageContent);
        imagesFiles.append("OverrideName", selectedImageTitleOverride);
        imagesFiles.append("Category", selectedImageCategory.toString());

        request(
            "/api/wiki-pages/images",
            {
                credentials: "same-origin",
                method: "POST",
                body: imagesFiles,
            },
            dispatch
        )
            .then(h.toJson)
            .then((res: { error: string }) => {
                if (res && res.error) {
                    h.error(res.error);
                } else {
                    dispatch(notifyGreen("Image saved into your Available Images."));
                }

                dispatch(SetWikiIsSaving(false));
            })
            .catch(error => {
                dispatch(SetWikiIsSaving(false));
                h.error(error && error.message ? error.message : "Error saving image.", error);
            });
    };

export const getImagePreview =
    (previewData: FormData) =>
    (dispatch: Dispatch<ISetLoadingPreview | ISetWikiPreview>): void => {
        dispatch(SetWikiPreviewLoading(true));

        return request(
            "/Images/GetUnsavedBase64StringFull",
            {
                credentials: "same-origin",
                method: "POST",
                body: previewData,
            },
            dispatch
        )
            .then(h.checkStatus)
            .then(h.toJson)
            .then((data: { result: string }) => {
                dispatch(SetWikiPreviewLoading(false));
                dispatch(SetWikiPreview(data.result));
            })
            .catch(error => {
                dispatch(SetWikiPreviewLoading(false));
                h.error("Error loading image preview. Perhaps you can try a smaller image.", error);
            });
    };
